import { motion } from "framer-motion";
import { Clapperboard, Mic, FolderOpen, ShieldCheck, Users2 } from "lucide-react";
import { BookDemoButton } from "@/pages/marketing/MarketingLayout";

const IMG_AI = "https://images.unsplash.com/photo-1518980120692-3cfe64c152d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGNsZWFuJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3ODc3Mzg4OTh8MA&ixlib=rb-4.1.0&q=85";

const SECTIONS = [
  {
    icon: Clapperboard,
    title: "AI Image & Video Generation",
    desc: "Create stunning visuals with fal.ai, B-roll with Kling, and AI avatar spokespersons with fal.ai.",
    accent: "text-blue-600 bg-blue-50",
    span: "md:col-span-8",
    withImage: true,
  },
  {
    icon: Mic,
    title: "AI Voice-Overs",
    desc: "Access a 200+ voice catalog powered by Inworld + fal.ai for professional audio.",
    accent: "text-emerald-600 bg-emerald-50",
    span: "md:col-span-4",
  },
  {
    icon: FolderOpen,
    title: "Shared Media Library",
    desc: "Organize brand assets, search stock photos (Pexels/Unsplash), and reuse existing media.",
    accent: "text-blue-600 bg-blue-50",
    span: "md:col-span-4",
  },
  {
    icon: ShieldCheck,
    title: "Brand Guideline Enforcement",
    desc: "Ensure all AI outputs strictly adhere to your specific brand voice and visuals.",
    accent: "text-emerald-600 bg-emerald-50",
    span: "md:col-span-4",
  },
  {
    icon: Users2,
    title: "Multi-Role Access",
    desc: "Admin, Editor, and Approver roles to match your team's workflow.",
    accent: "text-blue-600 bg-blue-50",
    span: "md:col-span-4",
  },
];

export default function Features() {
  return (
    <div data-testid="marketing-features-page">
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-6">
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter max-w-2xl"
          style={{ fontFamily: "Outfit" }}
          data-testid="features-page-title"
        >
          Powerful Features, Uncluttered Interface.
        </motion.h1>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
          {SECTIONS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`col-span-1 ${s.span} border border-slate-200 rounded-3xl bg-white overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col`}
                data-testid={`feature-section-card-${i}`}
              >
                {s.withImage && (
                  <div className="relative h-44">
                    <img src={IMG_AI} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
                  </div>
                )}
                <div className="p-8 flex-1">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-6 ${s.accent}`}>
                    <Icon size={20} strokeWidth={2} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight" style={{ fontFamily: "Outfit" }}>
                    {s.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="rounded-3xl bg-slate-900 px-8 md:px-16 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>
              Want a guided walkthrough of these tools?
            </h2>
            <p className="text-slate-400 max-w-md">We'll tailor the demo to your team's actual workflow.</p>
          </div>
          <BookDemoButton testId="features-cta-book-demo-button" className="shrink-0 !bg-white !text-slate-900 hover:!bg-slate-100" />
        </div>
      </section>
    </div>
  );
}
